import { parseTitleWithId } from '@/features/campaigns/v2/Sms/Templates/smsTemplateListFlowMapper';
import { SmsSender, SmsSenderStatus, SmsSenderType } from './types';

type SchemaProperty = {
  enum?: unknown[];
  enumNames?: unknown[];
  oneOf?: Array<{ const?: unknown; title?: string }>;
  default?: unknown;
  title?: string;
};

type PrepareStepParams = Record<string, { reactJsonSchemaForm?: { schema?: { properties?: Record<string, SchemaProperty> } } }> | null | undefined;

const NO_SENDERS_SENTINEL_PATTERNS = [
  /^--\s*no available (aliases?|senders?)\s*--$/i,
  /^no available (aliases?|senders?)$/i,
  /^--\s*(select|choose|none|n\/a|empty)?\s*--$/i,
  /^(select|choose)\s+(an?\s+)?(alias|sender)/i,
  /^none$/i,
  /^n\/a$/i,
];

export const isNoAvailableSendersSentinel = (value: string): boolean => {
  const normalized = value.trim();
  if (!normalized) {
    return true;
  }
  return NO_SENDERS_SENTINEL_PATTERNS.some((pattern) => pattern.test(normalized));
};

const readString = (record: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const normalizeSenderType = (value?: string): SmsSenderType => {
  if (!value) {
    return 'ALPHANUMERIC';
  }
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, '');
  if (normalized.includes('virtual') || normalized.includes('number') || normalized.startsWith('+') || /^\d+$/.test(normalized)) {
    return 'VIRTUAL_NUMBER';
  }
  return 'ALPHANUMERIC';
};

const normalizeSenderStatus = (value?: string): SmsSenderStatus => {
  if (!value) {
    return 'DRAFT';
  }
  const normalized = value.toUpperCase().replace(/[\s-]+/g, '_');
  if (normalized.includes('APPROV') || normalized.includes('VERIFIED') || normalized === 'ACTIVE') {
    return 'APPROVED';
  }
  if (normalized.includes('REGISTR') || normalized.includes('REVIEW') || normalized.includes('PENDING') || normalized.includes('PROGRESS')) {
    return 'IN_REGISTRATION';
  }
  if (normalized.includes('REJECT') || normalized.includes('FAIL') || normalized.includes('ERROR')) {
    return 'REJECTED';
  }
  if (normalized.includes('DRAFT')) {
    return 'DRAFT';
  }
  return 'DRAFT';
};

const looksLikeSenderRecord = (item: unknown): item is Record<string, unknown> => {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const record = item as Record<string, unknown>;
  return ['id', 'alias_id', 'sender_id', 'alias', 'sender'].some((key) => {
    const value = record[key];
    return (typeof value === 'string' && value.trim()) || (typeof value === 'number' && Number.isFinite(value));
  });
};

const mapRecordToSender = (item: Record<string, unknown>, fallbackId?: string, fallbackAlias?: string): SmsSender | null => {
  const id = readString(item, ['id', 'alias_id', 'sender_id', 'alias']) ?? fallbackId;
  if (!id || isNoAvailableSendersSentinel(id)) {
    return null;
  }

  const alias = readString(item, ['alias', 'name', 'sender', 'title']) ?? fallbackAlias ?? id;
  if (isNoAvailableSendersSentinel(alias)) {
    return null;
  }
  const typeValue = readString(item, ['type', 'alias_type', 'sender_type']);
  const registeredCompany = readString(item, ['registered_company', 'registeredCompany', 'company', 'company_name']);
  const statusValue = readString(item, ['status', 'alias_status', 'registration_status']);
  const registrationDate = readString(item, ['registration_date', 'registrationDate', 'created_at', 'createdAt', 'created_on']);

  return {
    id,
    alias,
    type: normalizeSenderType(typeValue ?? (alias.startsWith('+') ? 'VIRTUAL_NUMBER' : 'ALPHANUMERIC')),
    registeredCompany,
    status: normalizeSenderStatus(statusValue),
    registrationDate,
  };
};

/** Extract senders from select-style alias properties (oneOf / enum). */
const extractFromAliasProperty = (property: SchemaProperty | undefined): SmsSender[] => {
  if (!property) {
    return [];
  }

  if (Array.isArray(property.oneOf) && property.oneOf.length > 0) {
    return property.oneOf
      .map((option) => {
        const id = String(option.const ?? '');
        if (!id || isNoAvailableSendersSentinel(id)) {
          return null;
        }
        const alias = String(option.title ?? option.const ?? id);
        if (isNoAvailableSendersSentinel(alias)) {
          return null;
        }
        return {
          id,
          alias,
          type: normalizeSenderType(alias.startsWith('+') ? 'VIRTUAL_NUMBER' : 'ALPHANUMERIC'),
        } satisfies SmsSender;
      })
      .filter((item) => item !== null);
  }

  const enumValues = (property.enum ?? []).map(String).filter(Boolean);
  const enumNames = (property.enumNames ?? enumValues).map(String);

  return enumValues
    .map((id, index) => {
      if (isNoAvailableSendersSentinel(id)) {
        return null;
      }
      const alias = enumNames[index] ?? id;
      if (isNoAvailableSendersSentinel(alias)) {
        return null;
      }
      return {
        id,
        alias,
        type: normalizeSenderType(alias.startsWith('+') ? 'VIRTUAL_NUMBER' : 'ALPHANUMERIC'),
      } satisfies SmsSender;
    })
    .filter((item) => item !== null);
};

const extractFromArrayDefault = (value: unknown): SmsSender[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      console.log('item', item);
      if (typeof item === 'string') {
        if (isNoAvailableSendersSentinel(item)) {
          return null;
        }
        return {
          id: item,
          alias: item,
          type: normalizeSenderType(item.startsWith('+') ? 'VIRTUAL_NUMBER' : 'ALPHANUMERIC'),
        } satisfies SmsSender;
      }
      if (!item || typeof item !== 'object') {
        return null;
      }
      return mapRecordToSender(item as Record<string, unknown>);
    })
    .filter((item): item is SmsSender => item !== null);
};

const tryParseJsonObject = (value: string): Record<string, unknown> | null => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

/**
 * Alias List shape (mirrors Template List): each `alias_N` property is a readonly string with
 * `title: "alias (id)"` and `default` as secondary payload (plain text or JSON object).
 */
const extractFromStringAliasProperty = (key: string, property: SchemaProperty): SmsSender | null => {
  if (typeof property.default !== 'string') {
    return null;
  }

  const defaultValue = property.default;
  const title = typeof property.title === 'string' ? property.title.trim() : '';
  const parsed = title ? parseTitleWithId(title) : { name: key };
  const id = parsed.id || key;
  const alias = parsed.name || key;

  if (isNoAvailableSendersSentinel(id) || isNoAvailableSendersSentinel(alias)) {
    return null;
  }

  const jsonPayload = tryParseJsonObject(defaultValue);
  if (jsonPayload) {
    return mapRecordToSender(jsonPayload, id, alias);
  }

  return {
    id,
    alias,
    type: normalizeSenderType(alias.startsWith('+') ? 'VIRTUAL_NUMBER' : 'ALPHANUMERIC'),
    registeredCompany: defaultValue.trim() || undefined,
  };
};

const arrayDefaultLooksLikeSenderObjects = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  return value.some((item) => looksLikeSenderRecord(item));
};

/**
 * Extract SMS senders/aliases from Alias List prepare-step RJSF params.
 * Mirrors Template List: select-style `alias`, per-property string defaults, arrays, enum/oneOf.
 */
export const extractSmsSendersFromPrepareParams = (prepareParams: PrepareStepParams): SmsSender[] => {
  if (!prepareParams) {
    return [];
  }

  const stepKeys = Object.keys(prepareParams);
  const candidates: SmsSender[] = [];

  for (const stepKey of stepKeys) {
    const properties = prepareParams[stepKey]?.reactJsonSchemaForm?.schema?.properties ?? {};

    if (properties['alias']) {
      candidates.push(...extractFromAliasProperty(properties['alias']));
      if (typeof properties['alias'].default === 'string') {
        const sender = extractFromStringAliasProperty('alias', properties['alias']);
        if (sender) {
          candidates.push(sender);
        }
      } else if (properties['alias'].default !== undefined) {
        candidates.push(...extractFromArrayDefault(properties['alias'].default));
      }
    }

    for (const [key, property] of Object.entries(properties)) {
      if (key === 'alias' || !property || !/alias/i.test(key)) {
        continue;
      }

      if (typeof property.default === 'string') {
        const sender = extractFromStringAliasProperty(key, property);
        if (sender) {
          candidates.push(sender);
        }
      } else if (property.default !== undefined) {
        candidates.push(...extractFromArrayDefault(property.default));
      }

      if (property.enum || property.oneOf) {
        candidates.push(...extractFromAliasProperty(property));
      }
    }

    // Misnamed keys: array-of-objects that look like sender rows.
    for (const [key, property] of Object.entries(properties)) {
      if (/alias/i.test(key)) {
        continue;
      }
      if (arrayDefaultLooksLikeSenderObjects(property?.default)) {
        candidates.push(...extractFromArrayDefault(property.default));
      }
    }
  }

  const seen = new Set<string>();
  return candidates.filter((sender) => {
    if (seen.has(sender.id)) {
      return false;
    }
    seen.add(sender.id);
    return true;
  });
};
